export function includesIgnoringCase(testValue) {
  const testValueLower = testValue.toLowerCase();

  return cellValue =>
    cellValue !== null && cellValue.toString().toLowerCase().includes(testValueLower);
}

export function applyColumnFilters(rows, columns, filters = {}) {
  const filterEntries = Object.entries(filters);

  if (filterEntries.length === 0) {
    return rows;
  }

  // initialize custom filter functions per column, or default
  const filterTestFns = {};
  for (const [colKey, value] of filterEntries) {
    const col = columns.find(col => col.dataKey === colKey);
    const filterFn = col.filterFn || includesIgnoringCase;
    filterTestFns[colKey] = filterFn(value);
  }

  const filterCols = Object.keys(filters);

  return rows.filter(row => {
    return filterCols.every(colKey => {
      const filterTestFn = filterTestFns[colKey];
      const filterValue = filters[colKey];
      const value = row[colKey];

      try {
        return filterTestFn(value);
      } catch (ex) {
        console.error(
          `Error filtering rows: could not apply filter test to column '${colKey}', subject '${value}' filter value '${filterValue}'`
        );
        return false;
      }
    });
  });
}

export function applyTextSearch(rows, columns, searchText) {
  if (!searchText) {
    return rows;
  }

  const testFn = includesIgnoringCase(searchText);

  return rows.filter(row => {
    for (const column of columns) {
      const value = row[column.dataKey];
      let valueAsText = column.searchTextFn
        ? column.searchTextFn(value)
        : column.renderFn
          ? column.renderFn(value, row)
          : value;

      if (valueAsText?.constructor !== String) {
        valueAsText = value; // fix for when renderFn returns jsx object
      }

      if (value !== null && value !== undefined && testFn(valueAsText, searchText)) {
        return true;
      }
    }
    return false;
  });
}

export function sortByColumn(rows, columns, sortBy, sortDirection, tableSortFn) {
  let warnedMissingSortFn = false;

  rows = rows ? [...rows] : [];

  if (!sortBy || !sortDirection) {
    return rows;
  }

  // use sort function for this column if supplied, else use default:
  const colProps = columns.find(col => col.dataKey === sortBy);
  // column does not exist (this happens if the sortBy column is removed)
  if (!colProps) {
    return rows;
  }

  const sortFn =
    colProps.sortFn instanceof Function
      ? (a, b) => colProps.sortFn(a[sortBy], b[sortBy])
      : tableSortFn instanceof Function
        ? (a, b) => tableSortFn(a[sortBy], b[sortBy])
        : (a, b) => {
            if (colProps.numeric) {
              // numeric
              return (a[sortBy] || 0) - (b[sortBy] || 0);
            } else {
              // strings?
              let strA = a[sortBy];
              let strB = b[sortBy];

              const aType = strA?.constructor || null;
              const bType = strB?.constructor || null;

              // verify type / attempt string:
              if (aType !== String && bType !== String) {
                if (aType === Boolean || bType === Boolean) {
                  // detect boolean and sort accordingly:
                  strA = strA ? 1 : 0;
                  strB = strB ? 1 : 0;
                  return strA - strB;
                } else if (aType === Number || bType === Number) {
                  // detect numeric and sort accordingly:
                  return (strA || 0) - (strB || 0);
                } else if (aType === Array || bType === Array) {
                  // allow simple arrays to sort as if string:
                  strA = (strA || []).join();
                  strB = (strB || []).join();
                } else if (!aType && !bType) {
                  // both are nothing, return same:
                  return 0;
                } else {
                  if (!warnedMissingSortFn) {
                    console.warn(
                      `Please provide your own sort function for complex objects. Column name is '${sortBy}'`
                    );
                    warnedMissingSortFn = true;
                  }
                  return;
                }
              }

              if (aType === null) {
                strA = "";
              }
              if (bType === null) {
                strB = "";
              }
              // ignore upper and lowercase:
              strA = strA.toString().toUpperCase();
              strB = strB.toString().toUpperCase();
              if (strA < strB) {
                return -1;
              }
              if (strA > strB) {
                return 1;
              }
              // strings must be equal
              return 0;
            }
            // JAB: What about dates? If date is ISO string, sort by string works fine. If date is
            // number (or Date obj), sort by number works fine. Otherwise, it's a surprise!
            // Seriously though, if dates are to be formatted in non-ISO way, then keep it a number
            // or Date in the data and supply a renderFn to format it.
          };

  switch (sortDirection) {
    case "DESC":
      rows.sort((a, b) => sortFn(b, a));
      break;
    case "ASC":
    default:
      rows.sort((a, b) => sortFn(a, b));
      break;
  }
  return rows;
}
